import { differenceInDays, differenceInHours, differenceInMinutes, differenceInMonths, differenceInYears, isAfter, isBefore, isEqual } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import 'date-fns/locale/en-GB';

const languages = Array.from(navigator.languages);
export const localDateFormat = (date: Date | string, time = true) => {
  let targetDate = new Date(date);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (!time) {
    return targetDate.toLocaleDateString(languages, { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  return targetDate
    .toLocaleTimeString(languages, { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: 'numeric' })
    .replace(',', '');
};

export const isBetween = (date: Date, from: Date, to: Date, inclusivity = '()') => {
  if (!['()', '[]', '(]', '[)'].includes(inclusivity)) {
    throw new Error('Inclusivity parameter must be one of (), [], (], [)');
  }

  const isBeforeEqual = inclusivity[0] === '[',
    isAfterEqual = inclusivity[1] === ']';

  return (
    (isBeforeEqual ? isEqual(from, date) || isBefore(from, date) : isBefore(from, date)) &&
    (isAfterEqual ? isEqual(to, date) || isAfter(to, date) : isAfter(to, date))
  );
};

export const humanizeDate = (dateToConvert: Date | string): string => {
  const now: Date = new Date();
  const date = new Date(dateToConvert);
  const diffInMilliseconds: number = Math.abs(now.getTime() - date.getTime());
  const calculateLabel = (difference: number, unit: string) => `${difference} ${unit}${difference > 1 ? 's' : ''} ago`;

  const timeIntervals = [
    {
      threshold: 60 * 10 * 1000,
      calculateLabel: () => 'Just now'
    },
    {
      threshold: 60 * 60 * 1000,
      calculateLabel: () => calculateLabel(differenceInMinutes(now, date), 'minute')
    },
    {
      threshold: 24 * 60 * 60 * 1000,
      calculateLabel: () => calculateLabel(differenceInHours(now, date), 'hour')
    },
    {
      threshold: 30 * 24 * 60 * 60 * 1000,
      calculateLabel: () => calculateLabel(differenceInDays(now, date), 'day')
    },
    {
      threshold: 12 * 30 * 24 * 60 * 60 * 1000,
      calculateLabel: () => calculateLabel(differenceInMonths(now, date), 'month')
    },
    {
      threshold: Infinity,
      calculateLabel: () => calculateLabel(differenceInYears(now, date), 'year')
    }
  ];

  const interval = timeIntervals.find((interval) => diffInMilliseconds < interval.threshold)!;

  return interval.calculateLabel();
};

const splitTimeSpan = (timeSpan: string) => timeSpan.split(':').map((str) => parseInt(str, 10));

export const timeSpanToDate = (timeSpan: string, timeZone?: string): Date | null => {
  const [hours, minutes, seconds] = splitTimeSpan(timeSpan);

  const date = new Date();
  date.setHours(hours, minutes, seconds);
  return timeZone ? zonedTimeToUtc(date, timeZone) : date;
};
export const formatTime = (date: Date, timezoneOnly = false): string => {
  return date.toLocaleTimeString(
    languages,
    timezoneOnly ? { timeZoneName: 'short', hour: '2-digit', minute: 'numeric' } : { hour: '2-digit', minute: 'numeric' }
  );
};
